import React, { useState, useRef } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  FileText,
  Printer,
  Download,
  User,
  Calendar,
  Building2,
  Briefcase,
  UserCircle,
  Eye,
  ArrowLeft
} from 'lucide-react';
import { toast } from 'sonner';

const HRDocumentGenerator = () => {
  const [showPreview, setShowPreview] = useState(false);
  const printRef = useRef(null);
  
  const [employeeData, setEmployeeData] = useState({
    empName: '',
    empId: '',
    joinDate: '',
    department: '',
    position: '',
    reportingManager: '',
    empSignDate: '',
    hrSignDate: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEmployeeData(prev => ({ ...prev, [name]: value }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const handlePrint = () => {
    if (!employeeData.empName || !employeeData.empId) {
      toast.error('Please fill at least Employee Name and Employee ID');
      return;
    }
    
    const printContent = printRef.current;
    const printWindow = window.open('', '_blank');
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html lang="en">
      <head>
        <meta charset="UTF-8">
        <title>Employee Handbook - ${employeeData.empName}</title>
        <style>
          @page {
            size: A4;
            margin: 12mm 15mm;
          }
          
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          
          body {
            font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif;
            font-size: 9pt;
            line-height: 1.4;
            color: #1a1a1a;
            background: white;
          }
          
          .document {
            max-width: 210mm;
            margin: 0 auto;
          }
          
          /* Header */
          .doc-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding-bottom: 8px;
            border-bottom: 2px solid #1e3a5f;
            margin-bottom: 12px;
          }
          
          .logo-section {
            display: flex;
            align-items: center;
            gap: 10px;
          }
          
          .logo {
            width: 50px;
            height: 50px;
            object-fit: contain;
          }
          
          .company-name {
            font-size: 14pt;
            font-weight: 700;
            color: #1e3a5f;
            letter-spacing: -0.5px;
          }
          
          .company-tagline {
            font-size: 7pt;
            color: #666;
            letter-spacing: 0.5px;
          }
          
          .header-contact {
            text-align: right;
            font-size: 7pt;
            color: #666;
            line-height: 1.5;
          }
          
          /* Title */
          .doc-title {
            text-align: center;
            padding: 12px 0;
            margin-bottom: 12px;
            background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%);
            color: white;
            border-radius: 4px;
          }
          
          .doc-title h1 {
            font-size: 16pt;
            font-weight: 700;
            letter-spacing: 1px;
            margin-bottom: 2px;
          }
          
          .doc-title p {
            font-size: 8pt;
            opacity: 0.9;
          }
          
          /* Employee Info Box */
          .emp-info {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 8px;
            padding: 10px;
            background: #f8fafc;
            border: 1px solid #e2e8f0;
            border-left: 3px solid #2563eb;
            border-radius: 0 4px 4px 0;
            margin-bottom: 12px;
          }
          
          .emp-field {
            display: flex;
            flex-direction: column;
          }
          
          .emp-field label {
            font-size: 6.5pt;
            color: #64748b;
            text-transform: uppercase;
            letter-spacing: 0.3px;
            margin-bottom: 2px;
          }
          
          .emp-field .value {
            font-size: 9pt;
            font-weight: 600;
            color: #1e293b;
            padding-bottom: 2px;
            border-bottom: 1px solid #cbd5e1;
            min-height: 14px;
          }
          
          /* Welcome */
          .welcome {
            padding: 8px 10px;
            background: #f0fdf4;
            border-left: 3px solid #22c55e;
            border-radius: 0 4px 4px 0;
            margin-bottom: 12px;
          }
          
          .welcome h3 {
            font-size: 9pt;
            color: #166534;
            margin-bottom: 4px;
          }
          
          .welcome p {
            font-size: 8pt;
            color: #15803d;
            line-height: 1.5;
          }
          
          /* TOC */
          .toc {
            padding: 8px 10px;
            background: #f8fafc;
            border-radius: 4px;
            margin-bottom: 12px;
            page-break-after: avoid;
          }
          
          .toc h3 {
            font-size: 9pt;
            font-weight: 600;
            margin-bottom: 6px;
            color: #1e293b;
          }
          
          .toc-grid {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 2px 12px;
            font-size: 7.5pt;
            color: #475569;
          }
          
          /* Sections */
          .section {
            margin-bottom: 10px;
            page-break-inside: avoid;
          }
          
          .section-header {
            background: #1e3a5f;
            color: white;
            padding: 5px 10px;
            font-size: 9pt;
            font-weight: 600;
            border-radius: 3px;
            margin-bottom: 6px;
            page-break-after: avoid;
          }
          
          .section-content {
            padding: 0 8px;
            font-size: 8pt;
            line-height: 1.5;
          }
          
          .section-content h4 {
            font-size: 8pt;
            font-weight: 600;
            color: #334155;
            margin: 6px 0 3px 0;
          }
          
          .section-content ul, .section-content ol {
            margin: 4px 0 4px 14px;
          }
          
          .section-content li {
            margin-bottom: 2px;
          }
          
          .section-content p {
            margin: 4px 0;
          }
          
          /* Alert boxes */
          .alert-danger {
            background: #fef2f2;
            border-left: 3px solid #ef4444;
            padding: 6px 8px;
            margin: 6px 0;
            border-radius: 0 3px 3px 0;
          }
          
          .alert-danger p {
            color: #991b1b;
            font-weight: 600;
            font-size: 8pt;
            margin: 0;
          }
          
          .alert-warning {
            background: #fffbeb;
            border-left: 3px solid #f59e0b;
            padding: 8px 10px;
            margin: 10px 0;
            border-radius: 0 3px 3px 0;
          }
          
          .alert-warning h4 {
            color: #92400e;
            font-size: 8pt;
            font-weight: 600;
            margin-bottom: 3px;
          }
          
          .alert-warning p {
            color: #a16207;
            font-size: 7.5pt;
            margin: 0;
            line-height: 1.5;
          }
          
          /* Acknowledgment */
          .acknowledgment {
            border-top: 2px solid #1e3a5f;
            padding-top: 10px;
            margin-top: 12px;
            page-break-inside: avoid;
          }
          
          .acknowledgment h3 {
            font-size: 10pt;
            font-weight: 600;
            color: #1e3a5f;
            margin-bottom: 8px;
            text-align: center;
          }
          
          .ack-text {
            background: #f1f5f9;
            padding: 8px 10px;
            border-radius: 4px;
            margin-bottom: 10px;
            font-size: 7.5pt;
            line-height: 1.6;
          }
          
          .ack-text p {
            margin-bottom: 4px;
          }
          
          .ack-text ul {
            margin: 4px 0 0 14px;
          }
          
          .ack-text li {
            margin-bottom: 2px;
          }
          
          /* Signatures */
          .signatures {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 30px;
            margin-top: 15px;
          }
          
          .sig-block {
            text-align: center;
          }
          
          .sig-line {
            border-bottom: 1px solid #1e293b;
            height: 40px;
            margin-bottom: 4px;
          }
          
          .sig-label {
            font-size: 7pt;
            color: #64748b;
            margin-bottom: 2px;
          }
          
          .sig-date {
            font-size: 8pt;
            font-weight: 500;
            color: #1e293b;
          }
          
          /* Footer */
          .doc-footer {
            margin-top: 15px;
            padding-top: 8px;
            border-top: 1px solid #e2e8f0;
            text-align: center;
            font-size: 7pt;
            color: #64748b;
            page-break-inside: avoid;
          }
          
          .doc-footer strong {
            color: #1e3a5f;
          }
          
          /* Print optimizations */
          @media print {
            body { 
              -webkit-print-color-adjust: exact; 
              print-color-adjust: exact;
            }
            
            .section {
              page-break-inside: avoid;
            }
            
            .section-header {
              page-break-after: avoid;
            }
            
            .acknowledgment {
              page-break-inside: avoid;
            }
            
            .doc-footer {
              page-break-inside: avoid;
            }
          }
          
          /* Two column layout for compact sections */
          .two-col {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 8px 20px;
          }
          
          .two-col .section {
            margin-bottom: 0;
          }
        </style>
      </head>
      <body>
        ${printContent.innerHTML}
      </body>
      </html>
    `);
    
    printWindow.document.close();
    setTimeout(() => { printWindow.print(); }, 500);
  };

  const handleDownloadPDF = () => {
    if (!employeeData.empName || !employeeData.empId) {
      toast.error('Please fill at least Employee Name and Employee ID');
      return;
    }
    toast.info('Opening print dialog - Select "Save as PDF" to download');
    handlePrint();
  };

  // Preview styles (inline for the preview div)
  const previewStyles = `
    .hr-preview { font-family: 'Segoe UI', -apple-system, sans-serif; font-size: 11px; line-height: 1.4; color: #1a1a1a; padding: 24px; }
    .hr-preview .doc-header { display: flex; justify-content: space-between; align-items: center; padding-bottom: 10px; border-bottom: 2px solid #1e3a5f; margin-bottom: 16px; }
    .hr-preview .logo-section { display: flex; align-items: center; gap: 12px; }
    .hr-preview .logo { width: 60px; height: 60px; object-fit: contain; }
    .hr-preview .company-name { font-size: 18px; font-weight: 700; color: #1e3a5f; }
    .hr-preview .company-tagline { font-size: 10px; color: #666; }
    .hr-preview .header-contact { text-align: right; font-size: 10px; color: #666; line-height: 1.6; }
    .hr-preview .doc-title { text-align: center; padding: 16px; margin-bottom: 16px; background: linear-gradient(135deg, #1e3a5f, #2563eb); color: white; border-radius: 6px; }
    .hr-preview .doc-title h1 { font-size: 22px; font-weight: 700; letter-spacing: 1px; margin-bottom: 4px; }
    .hr-preview .doc-title p { font-size: 11px; opacity: 0.9; }
    .hr-preview .emp-info { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; padding: 14px; background: #f8fafc; border: 1px solid #e2e8f0; border-left: 4px solid #2563eb; border-radius: 0 6px 6px 0; margin-bottom: 16px; }
    .hr-preview .emp-field label { font-size: 9px; color: #64748b; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 3px; display: block; }
    .hr-preview .emp-field .value { font-size: 12px; font-weight: 600; color: #1e293b; padding-bottom: 4px; border-bottom: 1px solid #cbd5e1; min-height: 18px; }
    .hr-preview .welcome { padding: 12px 14px; background: #f0fdf4; border-left: 4px solid #22c55e; border-radius: 0 6px 6px 0; margin-bottom: 16px; }
    .hr-preview .welcome h3 { font-size: 12px; color: #166534; margin-bottom: 6px; }
    .hr-preview .welcome p { font-size: 11px; color: #15803d; line-height: 1.6; }
    .hr-preview .toc { padding: 12px 14px; background: #f8fafc; border-radius: 6px; margin-bottom: 16px; }
    .hr-preview .toc h3 { font-size: 12px; font-weight: 600; margin-bottom: 8px; color: #1e293b; }
    .hr-preview .toc-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 4px 16px; font-size: 10px; color: #475569; }
    .hr-preview .section { margin-bottom: 14px; }
    .hr-preview .section-header { background: #1e3a5f; color: white; padding: 8px 12px; font-size: 12px; font-weight: 600; border-radius: 4px; margin-bottom: 8px; }
    .hr-preview .section-content { padding: 0 10px; font-size: 11px; line-height: 1.6; }
    .hr-preview .section-content h4 { font-size: 11px; font-weight: 600; color: #334155; margin: 8px 0 4px 0; }
    .hr-preview .section-content ul, .hr-preview .section-content ol { margin: 6px 0 6px 18px; }
    .hr-preview .section-content li { margin-bottom: 3px; }
    .hr-preview .alert-danger { background: #fef2f2; border-left: 4px solid #ef4444; padding: 8px 10px; margin: 8px 0; border-radius: 0 4px 4px 0; }
    .hr-preview .alert-danger p { color: #991b1b; font-weight: 600; font-size: 11px; margin: 0; }
    .hr-preview .alert-warning { background: #fffbeb; border-left: 4px solid #f59e0b; padding: 10px 12px; margin: 12px 0; border-radius: 0 4px 4px 0; }
    .hr-preview .alert-warning h4 { color: #92400e; font-size: 11px; font-weight: 600; margin-bottom: 4px; }
    .hr-preview .alert-warning p { color: #a16207; font-size: 10px; margin: 0; line-height: 1.6; }
    .hr-preview .acknowledgment { border-top: 2px solid #1e3a5f; padding-top: 14px; margin-top: 16px; }
    .hr-preview .acknowledgment h3 { font-size: 14px; font-weight: 600; color: #1e3a5f; margin-bottom: 10px; text-align: center; }
    .hr-preview .ack-text { background: #f1f5f9; padding: 12px 14px; border-radius: 6px; margin-bottom: 14px; font-size: 10px; line-height: 1.7; }
    .hr-preview .ack-text ul { margin: 6px 0 0 18px; }
    .hr-preview .ack-text li { margin-bottom: 3px; }
    .hr-preview .signatures { display: grid; grid-template-columns: 1fr 1fr; gap: 40px; margin-top: 20px; }
    .hr-preview .sig-block { text-align: center; }
    .hr-preview .sig-line { border-bottom: 1px solid #1e293b; height: 50px; margin-bottom: 6px; }
    .hr-preview .sig-label { font-size: 10px; color: #64748b; margin-bottom: 3px; }
    .hr-preview .sig-date { font-size: 11px; font-weight: 500; color: #1e293b; }
    .hr-preview .doc-footer { margin-top: 20px; padding-top: 10px; border-top: 1px solid #e2e8f0; text-align: center; font-size: 10px; color: #64748b; }
    .hr-preview .doc-footer strong { color: #1e3a5f; }
  `;

  return (
    <div className="space-y-6" data-testid="hr-document-generator">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">HR Document Generator</h1>
          <p className="text-slate-500 text-sm mt-1">Generate Employee Handbook for new employees</p>
        </div>
        {showPreview && (
          <Button variant="outline" onClick={() => setShowPreview(false)}>
            <ArrowLeft className="w-4 h-4 mr-2" />Back to Form
          </Button>
        )}
      </div>

      {!showPreview ? (
        <>
          {/* Form */}
          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-6 flex items-center gap-2">
              <User className="w-5 h-5 text-indigo-600" />Employee Information
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="space-y-2">
                <Label htmlFor="empName" className="flex items-center gap-2">
                  <UserCircle className="w-4 h-4 text-slate-400" />Employee Name *
                </Label>
                <Input id="empName" name="empName" value={employeeData.empName} onChange={handleInputChange} placeholder="Enter full name" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="empId" className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-slate-400" />Employee ID *
                </Label>
                <Input id="empId" name="empId" value={employeeData.empId} onChange={handleInputChange} placeholder="e.g., EMP001" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="joinDate" className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-slate-400" />Date of Joining
                </Label>
                <Input id="joinDate" name="joinDate" type="date" value={employeeData.joinDate} onChange={handleInputChange} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="department" className="flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-slate-400" />Department
                </Label>
                <Input id="department" name="department" value={employeeData.department} onChange={handleInputChange} placeholder="e.g., Sales" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="position" className="flex items-center gap-2">
                  <Briefcase className="w-4 h-4 text-slate-400" />Position
                </Label>
                <Input id="position" name="position" value={employeeData.position} onChange={handleInputChange} placeholder="e.g., Sales Executive" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="reportingManager" className="flex items-center gap-2">
                  <User className="w-4 h-4 text-slate-400" />Reporting Manager
                </Label>
                <Input id="reportingManager" name="reportingManager" value={employeeData.reportingManager} onChange={handleInputChange} placeholder="Manager name" />
              </div>
            </div>
            
            <div className="border-t border-slate-200 mt-6 pt-6">
              <h3 className="text-sm font-medium text-slate-700 mb-4">Signature Dates</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="empSignDate">Employee Signature Date</Label>
                  <Input id="empSignDate" name="empSignDate" type="date" value={employeeData.empSignDate} onChange={handleInputChange} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="hrSignDate">HR Representative Date</Label>
                  <Input id="hrSignDate" name="hrSignDate" type="date" value={employeeData.hrSignDate} onChange={handleInputChange} />
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-4">
            <Button onClick={() => setShowPreview(true)} className="bg-indigo-600 hover:bg-indigo-700" disabled={!employeeData.empName || !employeeData.empId}>
              <Eye className="w-4 h-4 mr-2" />Preview Document
            </Button>
            <Button variant="outline" onClick={handlePrint} disabled={!employeeData.empName || !employeeData.empId}>
              <Printer className="w-4 h-4 mr-2" />Print Document
            </Button>
            <Button variant="outline" onClick={handleDownloadPDF} disabled={!employeeData.empName || !employeeData.empId}>
              <Download className="w-4 h-4 mr-2" />Download as PDF
            </Button>
          </div>
          <p className="text-sm text-slate-500">* Required fields</p>
        </>
      ) : (
        <>
          <div className="flex flex-wrap gap-4 sticky top-0 bg-slate-50 py-4 z-10">
            <Button onClick={handlePrint} className="bg-indigo-600 hover:bg-indigo-700">
              <Printer className="w-4 h-4 mr-2" />Print Document
            </Button>
            <Button variant="outline" onClick={handleDownloadPDF}>
              <Download className="w-4 h-4 mr-2" />Download as PDF
            </Button>
          </div>

          {/* Document Preview */}
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-lg">
            <style dangerouslySetInnerHTML={{__html: previewStyles}} />
            <div ref={printRef} className="hr-preview">
              <div className="document">
                {/* Header */}
                <div className="doc-header">
                  <div className="logo-section">
                    <img src="https://customer-assets.emergentagent.com/job_performance-hub-138/artifacts/bz4bc3tr_Logo.jpg" alt="Omnilink Solutions" className="logo" />
                    <div>
                      <div className="company-name">OMNILINK SOLUTIONS</div>
                      <div className="company-tagline">Professional IT Services & Solutions</div>
                    </div>
                  </div>
                  <div className="header-contact">
                    1219, Iconic Shyamal, Shyamal Cross Road, Ahmedabad<br/>
                    GJ, India - 380015<br/>
                    info@omnilinksolutions.net | +91-9587064194
                  </div>
                </div>

                {/* Title */}
                <div className="doc-title">
                  <h1>EMPLOYEE HANDBOOK</h1>
                  <p>Company Policies & Procedures • Effective January 2024</p>
                </div>

                {/* Employee Info */}
                <div className="emp-info">
                  <div className="emp-field"><label>Employee Name</label><div className="value">{employeeData.empName}</div></div>
                  <div className="emp-field"><label>Employee ID</label><div className="value">{employeeData.empId}</div></div>
                  <div className="emp-field"><label>Date of Joining</label><div className="value">{formatDate(employeeData.joinDate)}</div></div>
                  <div className="emp-field"><label>Department</label><div className="value">{employeeData.department}</div></div>
                  <div className="emp-field"><label>Position</label><div className="value">{employeeData.position}</div></div>
                  <div className="emp-field"><label>Reporting Manager</label><div className="value">{employeeData.reportingManager}</div></div>
                </div>

                {/* Welcome */}
                <div className="welcome">
                  <h3>Welcome to Omnilink Solutions!</h3>
                  <p>We are delighted to welcome you to our team. This handbook contains important information about our company policies, procedures, and expectations. Please read through all sections carefully and sign the acknowledgment at the end.</p>
                </div>

                {/* TOC */}
                <div className="toc">
                  <h3>Table of Contents</h3>
                  <div className="toc-grid">
                    <div>1. Employment Terms</div>
                    <div>6. Performance Management</div>
                    <div>11. IT & Communication</div>
                    <div>2. Code of Conduct</div>
                    <div>7. Training & Development</div>
                    <div>12. Disciplinary Procedures</div>
                    <div>3. Attendance & Punctuality</div>
                    <div>8. Health & Safety</div>
                    <div>13. Grievance Handling</div>
                    <div>4. Leave Policies</div>
                    <div>9. Anti-Harassment</div>
                    <div>14. Termination</div>
                    <div>5. Compensation & Benefits</div>
                    <div>10. Confidentiality</div>
                    <div>15. General Policies</div>
                  </div>
                </div>

                {/* Section 1 */}
                <div className="section">
                  <div className="section-header">1. EMPLOYMENT TERMS & CONDITIONS</div>
                  <div className="section-content">
                    <h4>1.1 Employment Status</h4>
                    <ul>
                      <li>All employment is subject to successful completion of probationary period (3 months)</li>
                      <li>Employment terms are governed by the signed employment contract</li>
                      <li>Any changes to employment terms require written agreement</li>
                    </ul>
                    <h4>1.2 Working Hours</h4>
                    <ul>
                      <li>Standard working hours: 7:00 PM to 4:30 AM, Monday to Friday</li>
                      <li>Lunch break: 1 hour (timing may vary by department)</li>
                      <li>Overtime work requires prior approval from reporting manager</li>
                      <li>Flexible working arrangements may be available subject to business needs</li>
                    </ul>
                  </div>
                </div>

                {/* Section 2 */}
                <div className="section">
                  <div className="section-header">2. CODE OF CONDUCT & ETHICS</div>
                  <div className="section-content">
                    <h4>2.1 Professional Behavior</h4>
                    <ul>
                      <li>Maintain highest standards of professional conduct at all times</li>
                      <li>Treat all colleagues, clients, and stakeholders with respect and dignity</li>
                      <li>Dress appropriately according to company dress code</li>
                      <li>Use professional language in all communications</li>
                    </ul>
                    <h4>2.2 Ethical Standards</h4>
                    <ul>
                      <li>Act with honesty, integrity, and transparency in all business dealings</li>
                      <li>Avoid conflicts of interest and report potential conflicts immediately</li>
                      <li>Comply with all applicable laws, regulations, and company policies</li>
                      <li>Protect company assets and use them responsibly</li>
                    </ul>
                  </div>
                </div>

                {/* Section 3 */}
                <div className="section">
                  <div className="section-header">3. ATTENDANCE & PUNCTUALITY</div>
                  <div className="section-content">
                    <ul>
                      <li>Regular attendance and punctuality are essential for business operations</li>
                      <li>Notify your manager immediately if you will be late or absent</li>
                      <li>Maintain accurate time records using company systems</li>
                      <li><strong>Late Coming Policy:</strong> 3 late arrivals in a month = 1 Half Day deduction</li>
                      <li>Excessive absenteeism or tardiness may result in disciplinary action</li>
                      <li>Medical certificates required for sick leave exceeding 1 consecutive day</li>
                    </ul>
                  </div>
                </div>

                {/* Section 4 */}
                <div className="section">
                  <div className="section-header">4. LEAVE POLICIES</div>
                  <div className="section-content">
                    <h4>4.1 Types of Leave</h4>
                    <ul>
                      <li><strong>Paid Leave:</strong> 1 paid leave per month, can be carried forward</li>
                      <li><strong>Advance Leave:</strong> No advance paid leave allowed for next month</li>
                      <li><strong>Sick Leave:</strong> With medical certificate if required</li>
                      <li><strong>Maternity/Paternity Leave:</strong> As per applicable labor laws</li>
                      <li><strong>Emergency Leave:</strong> Subject to management approval</li>
                    </ul>
                    <h4>4.2 Leave Application Process</h4>
                    <ul>
                      <li>Submit leave applications at least 48 hours in advance (except emergencies)</li>
                      <li>Obtain written approval from reporting manager before taking leave</li>
                      <li>Ensure proper handover of responsibilities before extended leave</li>
                    </ul>
                  </div>
                </div>

                {/* Section 5 */}
                <div className="section">
                  <div className="section-header">5. COMPENSATION & BENEFITS</div>
                  <div className="section-content">
                    <h4>5.1 Salary & Payment</h4>
                    <ul>
                      <li>Salaries are paid monthly on or before 11th of next month</li>
                      <li>All statutory deductions will be made as per applicable laws</li>
                      <li>Salary revisions based on performance and company policies</li>
                    </ul>
                    <h4>5.2 Benefits</h4>
                    <ul>
                      <li>Professional development opportunities and training programs</li>
                      <li>Performance-based incentives and bonuses</li>
                    </ul>
                  </div>
                </div>

                {/* Section 6 */}
                <div className="section">
                  <div className="section-header">6. PERFORMANCE MANAGEMENT</div>
                  <div className="section-content">
                    <ul>
                      <li>Regular performance reviews conducted monthly and quarterly</li>
                      <li>Clear goal setting and performance metrics established</li>
                      <li>Continuous feedback and coaching provided by managers</li>
                      <li>Performance improvement plans for underperforming employees</li>
                      <li>Recognition and rewards for exceptional performance</li>
                    </ul>
                  </div>
                </div>

                {/* Section 7 */}
                <div className="section">
                  <div className="section-header">7. TRAINING & DEVELOPMENT</div>
                  <div className="section-content">
                    <ul>
                      <li>Comprehensive onboarding program for all new employees</li>
                      <li>Regular skill development and training opportunities</li>
                      <li>Internal mentoring and knowledge sharing programs</li>
                      <li>Career development planning and growth opportunities</li>
                    </ul>
                  </div>
                </div>

                {/* Section 8 */}
                <div className="section">
                  <div className="section-header">8. HEALTH & SAFETY</div>
                  <div className="section-content">
                    <ul>
                      <li>Company is committed to providing a safe and healthy work environment</li>
                      <li>Report all accidents, injuries, and safety hazards immediately</li>
                      <li>Follow all safety procedures and use protective equipment when required</li>
                      <li>Participate in safety training and emergency drills</li>
                      <li>Maintain clean and organized workspaces</li>
                    </ul>
                  </div>
                </div>

                {/* Section 9 */}
                <div className="section">
                  <div className="section-header">9. ANTI-HARASSMENT & DISCRIMINATION</div>
                  <div className="section-content">
                    <div className="alert-danger">
                      <p>ZERO TOLERANCE POLICY</p>
                    </div>
                    <ul>
                      <li>Strictly prohibited: harassment, discrimination, or retaliation of any kind</li>
                      <li>Equal opportunity employer regardless of race, gender, religion, age, or disability</li>
                      <li>Report incidents immediately to HR or management</li>
                      <li>Confidential investigation process for all complaints</li>
                      <li>Appropriate disciplinary action for violations up to termination</li>
                    </ul>
                  </div>
                </div>

                {/* Section 10 */}
                <div className="section">
                  <div className="section-header">10. CONFIDENTIALITY & DATA PROTECTION</div>
                  <div className="section-content">
                    <h4>10.1 Confidential Information</h4>
                    <ul>
                      <li>Protect all confidential company and client information</li>
                      <li>Sign and comply with non-disclosure agreements</li>
                      <li>Do not share sensitive information with unauthorized persons</li>
                      <li>Return all confidential materials upon termination</li>
                    </ul>
                    <h4>10.2 Data Protection</h4>
                    <ul>
                      <li>Comply with all data protection laws and regulations</li>
                      <li>Handle personal data responsibly and securely</li>
                      <li>Report data breaches immediately to IT and management</li>
                      <li>Use strong passwords and follow security protocols</li>
                    </ul>
                  </div>
                </div>

                {/* Section 11 */}
                <div className="section">
                  <div className="section-header">11. IT & COMMUNICATION POLICIES</div>
                  <div className="section-content">
                    <h4>11.1 IT Usage</h4>
                    <ul>
                      <li>Company IT resources are for business use primarily</li>
                      <li>Limited personal use permitted during break times</li>
                      <li>Prohibited: accessing inappropriate content, downloading unauthorized software</li>
                      <li>Regular monitoring of IT usage for security and compliance</li>
                    </ul>
                    <h4>11.2 Communication</h4>
                    <ul>
                      <li>Use professional email signatures and communication standards</li>
                      <li>Respond to internal communications within 24 hours</li>
                      <li>Follow social media guidelines when representing the company</li>
                    </ul>
                  </div>
                </div>

                {/* Section 12 */}
                <div className="section">
                  <div className="section-header">12. DISCIPLINARY PROCEDURES</div>
                  <div className="section-content">
                    <h4>12.1 Progressive Discipline</h4>
                    <ol>
                      <li>Verbal warning and counseling</li>
                      <li>Written warning with improvement plan</li>
                      <li>Final written warning</li>
                      <li>Suspension (with or without pay)</li>
                      <li>Termination of employment</li>
                    </ol>
                    <h4>12.2 Serious Misconduct</h4>
                    <p>Serious violations may result in immediate termination without progressive discipline.</p>
                  </div>
                </div>

                {/* Section 13 */}
                <div className="section">
                  <div className="section-header">13. GRIEVANCE HANDLING</div>
                  <div className="section-content">
                    <ul>
                      <li>Open door policy for raising concerns and grievances</li>
                      <li>Formal grievance procedure available through HR department</li>
                      <li>Confidential and fair investigation of all complaints</li>
                      <li>No retaliation against employees raising legitimate concerns</li>
                      <li>Timely resolution and communication of outcomes</li>
                    </ul>
                  </div>
                </div>

                {/* Section 14 */}
                <div className="section">
                  <div className="section-header">14. TERMINATION PROCEDURES</div>
                  <div className="section-content">
                    <h4>14.1 Notice Period</h4>
                    <ul>
                      <li>Standard notice period as per employment contract</li>
                      <li>Company may accept payment in lieu of notice</li>
                      <li>Immediate termination for serious misconduct</li>
                    </ul>
                    <h4>14.2 Exit Process</h4>
                    <ul>
                      <li>Complete handover of responsibilities and projects</li>
                      <li>Return all company property and access cards</li>
                      <li>Exit interview with HR department</li>
                      <li>Final settlement as per company policy</li>
                    </ul>
                  </div>
                </div>

                {/* Section 15 */}
                <div className="section">
                  <div className="section-header">15. GENERAL POLICIES</div>
                  <div className="section-content">
                    <ul>
                      <li><strong>Smoking:</strong> Prohibited in all company premises</li>
                      <li><strong>Visitors:</strong> All visitors must be registered and escorted</li>
                      <li><strong>Personal Property:</strong> Company not liable for personal items</li>
                      <li><strong>Parking:</strong> Follow designated parking arrangements</li>
                      <li><strong>Environmental:</strong> Participate in green initiatives</li>
                      <li><strong>Policy Updates:</strong> Policies subject to change with notice</li>
                    </ul>
                  </div>
                </div>

                {/* Important Notice */}
                <div className="alert-warning">
                  <h4>Important Notice</h4>
                  <p>This handbook provides general guidelines and is not a contract of employment. The company reserves the right to modify, update, or change these policies at any time with appropriate notice. For specific questions about any policy, please contact the HR department.</p>
                </div>

                {/* Acknowledgment */}
                <div className="acknowledgment">
                  <h3>EMPLOYEE ACKNOWLEDGMENT</h3>
                  <div className="ack-text">
                    <p>I acknowledge that I have received, read, and understood the Employee Handbook of Omnilink Solutions. I understand that:</p>
                    <ul>
                      <li>I am expected to comply with all policies and procedures outlined in this handbook</li>
                      <li>Violation of these policies may result in disciplinary action up to and including termination</li>
                      <li>The company reserves the right to modify these policies with appropriate notice</li>
                      <li>This handbook does not constitute a contract of employment</li>
                      <li>I can contact HR for clarification on any policy matters</li>
                    </ul>
                  </div>

                  <div className="signatures">
                    <div className="sig-block">
                      <div className="sig-line"></div>
                      <div className="sig-label">Employee Signature</div>
                      <div className="sig-date">{formatDate(employeeData.empSignDate) || '________________'}</div>
                    </div>
                    <div className="sig-block">
                      <div className="sig-line"></div>
                      <div className="sig-label">HR Representative</div>
                      <div className="sig-date">{formatDate(employeeData.hrSignDate) || '________________'}</div>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="doc-footer">
                  <strong>OMNILINK SOLUTIONS</strong> | 1219, Iconic Shyamal, Shyamal Cross Road, Ahmedabad, GJ, India - 380015<br/>
                  Email: info@omnilinksolutions.net | Phone: +91-9587064194<br/>
                  © 2024 Omnilink Solutions. All rights reserved. | This document is confidential.
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default HRDocumentGenerator;
